
import React, { useState, useMemo, useEffect } from 'react';
import { ChevronLeft, ChevronRight, Calendar, ExternalLink, Trophy, MapPin, Clock, Star, Filter, Search, Grid, List, Music } from 'lucide-react';
import { Button } from './ui/button';
import { eventsData } from '../data/events2026';

export const UpcomingEvents = () => {
  const [currentMonthIndex, setCurrentMonthIndex] = useState(0);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [viewMode, setViewMode] = useState('grid');
  const [searchQuery, setSearchQuery] = useState('');

  // Dynamically filter out past events so 'Upcoming' remains accurate
  const activeEventsData = useMemo(() => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return eventsData.map(monthData => ({
      ...monthData,
      events: monthData.events.filter(event => {
        const eventDate = event.endDate ? new Date(event.endDate) : new Date(event.startDate);
        return eventDate >= today;
      })
    })).filter(monthData => monthData.events.length > 0);
  }, []);

  const categories = {
    all: 'All Events',
    tennis: 'Tennis',
    formula1: 'Formula 1',
    football: 'Football',
    cricket: 'Cricket',
    entertainment: 'Entertainment',
    golf: 'Golf',
    rugby: 'Rugby',
    horseracing: 'Horse Racing',
    basketball: 'Basketball',
    darts: 'Darts',
    badminton: 'Badminton',
    motorsport: 'Motorsport',
    kabaddi: 'Kabaddi'
  };

  const categoryIcons = {
    cricket: '🏏',
    tennis: '🎾',
    formula1: '🏎️',
    football: '⚽',
    entertainment: '🎵',
    golf: '⛳',
    rugby: '🏉',
    horseracing: '🐎',
    basketball: '🏀',
    darts: '🎯',
    badminton: '🏸',
    motorsport: '🏍️',
    kabaddi: '🤼'
  };

  const currentMonth = useMemo(() => {
    if (currentMonthIndex >= 0 && currentMonthIndex < activeEventsData.length) {
      return activeEventsData[currentMonthIndex];
    }
    return activeEventsData.length > 0 ? activeEventsData[0] : { month: '', year: '', events: [] };
  }, [currentMonthIndex, activeEventsData]);

  const filteredEvents = useMemo(() => {
    // If search or category filter is active, show all matching events across all months
    if (selectedCategory !== 'all' || searchQuery) {
      let allEvents = activeEventsData.flatMap(monthData =>
        monthData.events.map(event => ({
          ...event,
          month: monthData.month,
          year: monthData.year
        }))
      );

      // Apply category filter
      if (selectedCategory !== 'all') {
        allEvents = allEvents.filter(event => event.category === selectedCategory);
      }

      // Apply search filter
      if (searchQuery) {
        allEvents = allEvents.filter(event =>
          event.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.venue.toLowerCase().includes(searchQuery.toLowerCase()) ||
          event.description.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      return allEvents;
    }

    // Otherwise, show only the current month's events
    return currentMonth.events.map(event => ({
      ...event,
      month: currentMonth.month,
      year: currentMonth.year
    }));
  }, [selectedCategory, searchQuery, currentMonthIndex, currentMonth]);

  const goToPreviousMonth = () => {
    setCurrentMonthIndex(prev => Math.max(0, prev - 1));
  };

  const goToNextMonth = () => {
    setCurrentMonthIndex(prev => Math.min(activeEventsData.length - 1, prev + 1));
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  const EventCard = ({ event, isListView = false }) => (
    <div className={`bg-white rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 border border-gray-100 ${isListView ? 'flex' : ''}`}>
      <div className={`p-6 ${isListView ? 'flex-1' : ''}`}>
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center space-x-2">
            <span className="text-2xl">{categoryIcons[event.category] || '🏆'}</span>
            <span className={`px-2 py-1 text-xs font-medium rounded-full ${event.category === 'cricket' ? 'bg-orange-100 text-orange-800' :
              event.category === 'tennis' ? 'bg-yellow-100 text-yellow-800' :
                event.category === 'formula1' || event.category === 'motorsport' ? 'bg-red-100 text-red-800' :
                  event.category === 'football' ? 'bg-green-100 text-green-800' :
                    event.category === 'golf' ? 'bg-green-100 text-green-800' :
                      event.category === 'rugby' ? 'bg-blue-100 text-blue-800' :
                        event.category === 'horseracing' ? 'bg-amber-100 text-amber-800' :
                          event.category === 'kabaddi' ? 'bg-orange-100 text-orange-800' :
                            event.category === 'badminton' ? 'bg-teal-100 text-teal-800' :
                              'bg-purple-100 text-purple-800'
              }`}>
              {categories[event.category]}
            </span>
          </div>
          <div className="flex items-center space-x-2">
            {event.ticketsAvailable ? (
              <span className="flex items-center text-green-600 text-xs">
                <Star className="w-3 h-3 mr-1" />
                Available
              </span>
            ) : (
              <span className="flex items-center text-red-600 text-xs">
                <Clock className="w-3 h-3 mr-1" />
                Sold Out
              </span>
            )}
          </div>
        </div>

        <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2">
          {event.name}
        </h3>

        <div className="space-y-2 mb-4">
          <div className="flex items-center text-gray-600 text-sm">
            <Calendar className="w-4 h-4 mr-2 text-blue-500" />
            <span>
              {event.endDate
                ? `${formatDate(event.startDate)} - ${formatDate(event.endDate)}`
                : formatDate(event.startDate)
              }
            </span>
          </div>

          <div className="flex items-center text-gray-600 text-sm">
            <MapPin className="w-4 h-4 mr-2 text-red-500" />
            <span className="line-clamp-1">{event.venue}</span>
          </div>
        </div>

        <p className="text-gray-600 text-sm mb-4 line-clamp-2">
          {event.description}
        </p>

        <div className="flex items-center justify-between">
          <a
            href={event.url}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition-colors duration-200"
          >
            View Details
            <ExternalLink className="w-3 h-3 ml-1" />
          </a>

          <div className="flex items-center space-x-2">
            <Trophy className="w-4 h-4 text-yellow-500" />
            <span className="text-xs text-gray-500 font-medium">Premium Event</span>
          </div>
        </div>
      </div>
    </div>
  );

  return (
    <section id="upcoming-events" className="py-20 bg-gradient-to-br from-gray-50 to-white">
      <div className="container mx-auto px-6">
        <div className="text-center mb-12">
          <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
            Upcoming Events
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Discover premium sporting events for the 2026 season.
            Your gateway to the world's most exclusive experiences.
          </p>
        </div>

        {/* Controls Section */}
        <div className="bg-white rounded-2xl shadow-lg p-6 mb-8 border border-gray-100">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between space-y-4 lg:space-y-0">

            {/* Month Navigation */}
            <div className="flex items-center justify-between w-full lg:w-auto lg:min-w-[320px]">
              <Button
                onClick={goToPreviousMonth}
                disabled={currentMonthIndex === 0 || selectedCategory !== 'all' || searchQuery}
                variant="outline"
                size="sm"
                className="p-2 flex-shrink-0"
              >
                <ChevronLeft className="w-4 h-4" />
              </Button>

              <div className="text-center flex-1 mx-4">
                <h3 className="text-xl lg:text-2xl font-bold text-gray-900">
                  {selectedCategory !== 'all' || searchQuery ? 'All Events' : `${currentMonth.month} ${currentMonth.year}`}
                </h3>
                <p className="text-xs lg:text-sm text-gray-600">
                  {filteredEvents.length} event{filteredEvents.length !== 1 ? 's' : ''} found
                </p>
              </div>

              <Button
                onClick={goToNextMonth}
                disabled={currentMonthIndex === activeEventsData.length - 1 || selectedCategory !== 'all' || searchQuery || activeEventsData.length === 0}
                variant="outline"
                size="sm"
                className="p-2 flex-shrink-0"
              >
                <ChevronRight className="w-4 h-4" />
              </Button>
            </div>

            {/* Search and Filters */}
            <div className="flex flex-col sm:flex-row items-stretch sm:items-center space-y-2 sm:space-y-0 sm:space-x-4">
              {/* Search */}
              <div className="relative">
                <Search className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search events..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm w-full sm:w-64"
                />
              </div>

              {/* Category Filter */}
              <div className="relative">
                <Filter className="w-4 h-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <select
                  value={selectedCategory}
                  onChange={(e) => setSelectedCategory(e.target.value)}
                  className="pl-10 pr-8 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm appearance-none bg-white w-full sm:w-auto"
                >
                  {Object.entries(categories).map(([key, label]) => (
                    <option key={key} value={key}>{label}</option>
                  ))}
                </select>
              </div>

              {/* View Mode Toggle */}
              <div className="flex border border-gray-200 rounded-lg overflow-hidden">
                <button
                  onClick={() => setViewMode('grid')}
                  className={`p-2 transition-colors duration-200 ${viewMode === 'grid'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <Grid className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setViewMode('list')}
                  className={`p-2 transition-colors duration-200 ${viewMode === 'list'
                    ? 'bg-blue-500 text-white'
                    : 'bg-white text-gray-600 hover:bg-gray-50'
                    }`}
                >
                  <List className="w-4 h-4" />
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Events Grid/List */}
        <div className={
          viewMode === 'grid'
            ? 'grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8'
            : 'space-y-6'
        }>
          {filteredEvents.length > 0 ? (
            filteredEvents.map((event) => (
              <EventCard
                key={event.id}
                event={event}
                isListView={viewMode === 'list'}
              />
            ))
          ) : (
            <div className="col-span-full text-center py-12">
              <div className="text-gray-400 mb-4">
                <Calendar className="w-16 h-16 mx-auto mb-4" />
                <h3 className="text-xl font-semibold text-gray-600 mb-2">
                  No events found
                </h3>
                <p className="text-gray-500">
                  Try adjusting your search or filter criteria.
                </p>
              </div>
            </div>
          )}
        </div>

        {/* Quick Stats */}
        <div className="mt-12 bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white">
          <div className="text-center mb-6">
            <h3 className="text-2xl font-bold mb-2">Event Calendar Stats</h3>
            <p className="opacity-90">Your premium event schedule for the 2026 season</p>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                {activeEventsData.reduce((total, month) => total + month.events.length, 0)}
              </div>
              <div className="text-sm opacity-80">Total Events</div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                {Object.keys(categories).length - 1}
              </div>
              <div className="text-sm opacity-80">Categories</div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                {activeEventsData.length}
              </div>
              <div className="text-sm opacity-80">Months Covered</div>
            </div>

            <div className="text-center">
              <div className="text-3xl font-bold mb-1">
                {activeEventsData.reduce((count, month) =>
                  count + month.events.filter(event => event.ticketsAvailable).length, 0
                )}
              </div>
              <div className="text-sm opacity-80">Available Now</div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};
